import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";

import { closeBatchMode, closeDetails, selectGroupDetails } from "@dashboard/devices/store/index";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SidePanel } from "@dashboard/shared/components/SidePanel/SidePanel";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { TableRow, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { SecondaryButton } from "@shared/CustomButton";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import { Tooltip } from "@shared/Tooltip";
import {
    ColumnFiltersState,
    ExpandedState,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { GroupFilterHeader } from "../shared/components/GroupFilterHeader/GroupFilterHeader";
import { getBadgeContent } from "../shared/Filtering.utils";

import CreateGroupDialog from "./components/CreateGroupDialog";
import { GroupTableRow } from "./components/GroupTableRow";
import { selectGroups } from "./store/selectors";
import {
    buildGroupTableDataForSelectionFilter,
    customGlobalFilterFn,
    getColumns,
    getExpandedStateForGroup,
    getGroupIdsFromNameColumnFilter,
    getGroupsTableData,
    GroupOverviewData,
} from "./GroupPage.utils";
import { useStyles } from "./GroupsPage.style";

const GroupsPage = () => {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(UserSettingsService.getGroupPageColumnFilters());
    const [expanded, setExpanded] = React.useState<ExpandedState>(UserSettingsService.getGroupPageExpanded());
    const [globalFilter, setGlobalFilter] = React.useState(UserSettingsService.getGroupPageSearch());
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

    const { t } = useTranslation(["pages", "common"]);
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const { groupAbility } = PermissionService();

    const groups = useSelector(selectGroups);
    const { data: workspace } = useCurrentWorkspace();

    const isExpired = useCurrentSubscriptionExpired();
    const details = useSelector(selectGroupDetails);

    const selectedNameFilterIds = React.useMemo(() => getGroupIdsFromNameColumnFilter(columnFilters), [columnFilters]);

    const tableData = React.useMemo(() => {
        const base = getGroupsTableData(groups, workspace?.defaultGroupId);
        if (selectedNameFilterIds.length === 0) {
            return base;
        }
        return buildGroupTableDataForSelectionFilter(base, groups, selectedNameFilterIds);
    }, [groups, workspace?.defaultGroupId, selectedNameFilterIds]);

    const state = React.useMemo(() => ({ globalFilter, expanded, columnFilters }), [expanded, globalFilter, columnFilters]);

    const columns = React.useMemo(() => getColumns(globalFilter), [globalFilter]);

    const table = useReactTable<GroupOverviewData>({
        data: tableData,
        columns,
        state,
        globalFilterFn: customGlobalFilterFn,
        getColumnCanGlobalFilter: () => true,
        getRowId: (row) => String(row.id),
        getSubRows: (row) => row.children,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onExpandedChange: setExpanded,
        getExpandedRowModel: getExpandedRowModel(),
    });

    const setFilters = (id: "name", values: Record<string, boolean>) => {
        setColumnFilters((prev) => {
            const prevFiltersWithoutId = prev.filter((filter) => filter.id !== id);
            const value = Object.keys(_.pickBy(values, (item) => item));
            return [{ id, value }, ...prevFiltersWithoutId];
        });
    };
    const getTooltipTitle = () => {
        if (isExpired) {
            return t("pages:groupPage.tooltip.subscriptionExpired");
        }
        if (groupAbility(null, workspace).cannot("create", "Group")) {
            return t("pages:groupPage.tooltip.cannotCreateGroup");
        }
        return "";
    };

    const handleResetFilter = () => {
        setColumnFilters([]);
        setGlobalFilter("");
        table.setPageIndex(0);
    };

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("groupPage.columnFilters", columnFilters);
    }, [columnFilters]);

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("groupPage.expanded", expanded);
    }, [expanded]);

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("groupPage.search", globalFilter);
    }, [globalFilter]);

    React.useEffect(() => {
        if (details?.selectedId && tableData.length > 0) {
            setExpanded((prev) => getExpandedStateForGroup(details.selectedId, groups, prev));
        }
    }, [details?.selectedId, tableData]);

    React.useEffect(() => {
        return () => {
            dispatch(closeDetails());
            dispatch(closeBatchMode());
        };
    }, []);

    return (
        <PageContainer className={classes.modalContainer}>
            <ExpiredWrapper>
                <React.Fragment>
                    <div className={classes.searchContainer}>
                        <QueryBuilderField
                            name="group-search"
                            autoComplete="group-search-field"
                            placeholder={t("common:button.search")}
                            value={globalFilter}
                            onChange={setGlobalFilter}
                            onClear={() => setGlobalFilter("")}
                        />
                        <Tooltip title={getTooltipTitle()} hide={groupAbility(null, workspace).can("create", "Group")}>
                            <SecondaryButton
                                onClick={() => setCreateDialogOpen(true)}
                                disabled={groupAbility(null, workspace).cannot("create", "Group")}
                                startIcon={<i className="fa-solid fa-plus" />}
                                size="small"
                                name="add-group"
                            >
                                <Typography>{t("pages:groupPage.button.addGroup")}</Typography>
                            </SecondaryButton>
                        </Tooltip>
                    </div>
                    <MinimalTableContainer>
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            if (header.id === "name") {
                                                return (
                                                    <GroupFilterHeader
                                                        key={header.id}
                                                        header={header}
                                                        table={table}
                                                        badgeContent={getBadgeContent("name", columnFilters)}
                                                        onSubmit={(values) => setFilters("name", values)}
                                                    />
                                                );
                                            }
                                            return <Header key={header.id} {...header} />;
                                        })}
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody<GroupOverviewData>
                                table={table}
                                filter={{
                                    isActive: !_.isEmpty(columnFilters) || !_.isEmpty(globalFilter),
                                    onResetFilter: handleResetFilter,
                                }}
                                noDataMessage={t("pages:groupPage.table.noGroups")}
                            >
                                {table.getRowModel().rows.map((row) => (
                                    <GroupTableRow key={row.id} {...row} />
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                    <CreateGroupDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />
                </React.Fragment>
            </ExpiredWrapper>
            <SidePanel />
        </PageContainer>
    );
};

export default withMetaDescription(GroupsPage, {
    title: i18next.t("pages:groupPage.metaTitle"),
    description: i18next.t("pages:groupPage.metaDescription"),
});
